import { ref } from "vue";
import type { UnwrapRef } from "vue";
import { useCookie } from "nuxt/app";
import type { HeaderRequest, IApiResponse } from "../types/general";

export default function useCustomFetch<T>(url: string | (() => string)) {
  const config = useRuntimeConfig();
  const currency = useCookie("currency").value || config.public.defaultCurrency;
  const locale = useCookie("locale").value || config.public.defaultLocale;
  const accessToken = useCookie("accessToken").value;

  const data = ref<IApiResponse<T> | null>(null);
  const errorMessage = ref<string | null>(null);
  const baseURL = config.public.apiBaseUrl;
  // || "http://161.35.193.238:9100"
  const headers = ref<HeaderRequest>({
    "X-CLIENT-ID": config.public.xClientId,
    Accept: "application/json",
    "Accept-Language": locale,
    "Sec-Fetch-Dest": "empty",
    "Sec-Fetch-Mode": "cors",
    "Sec-Fetch-Site": "same-site",
    "Accept-Currency": currency,
  });

  if (accessToken) headers.value["authorization"] = "bearer " + accessToken;

  const fetchData = async () => {
    try {
      const res = await fetch(`${baseURL}${url}`, {
        headers: headers.value,
      });
      data.value = await res.json();
    } catch (err) {
      console.log(err);
    }
  };

  const postData = async <U>(body: U): Promise<void> => {
    try {
      const res = await fetch(`${baseURL}${url}`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          ...headers.value,
        },
      });
      //TODO move it to a handler
      const parsedData: IApiResponse<UnwrapRef<T>> = await res.json();
      if (res.status === 200) {
        Object.keys(parsedData).includes("data")
          ? (data.value = {
              status: 200,
              ...parsedData,
            })
          : (data.value = {
              status: 200,
              data: parsedData as UnwrapRef<T>,
            });
      } else {
        data.value = {
          status: res.status,
          ...parsedData,
        };
      }
    } catch (err) {
      console.error(err);
    }
  };
  const patchData = async <U>(body: U): Promise<void> => {
    try {
      const res = await fetch(`${baseURL}${url}`, {
        method: "PATCH",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          ...headers.value,
        },
      });
      const parsedData: IApiResponse<UnwrapRef<T>> = await res.json();
      if (res.status === 200) {
        data.value = {
          status: 200,
          ...parsedData,
        };
      } else {
        data.value = {
          status: res.status,
          ...parsedData,
        };
      }
    } catch (err) {
      console.error(err);
    }
  };
  const deleteData = async () => {
    try {
      const res = await fetch(`${baseURL}${url}`, {
        headers: headers.value,
        method: "DELETE",
      });
      data.value = await res.json();
    } catch (err) {
      console.log(err);
    }
  };

  const putData = async <U>(body: U): Promise<void> => {
    try {
      const res = await fetch(`${baseURL}${url}`, {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          ...headers.value,
        },
      });
      const parsedData: IApiResponse<UnwrapRef<T>> = await res.json();
      if (res.status === 200) {
        data.value = {
          status: 200,
          ...parsedData,
        };
      } else {
        data.value = {
          status: res.status,
          ...parsedData,
        };
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateHeader = async (key: keyof HeaderRequest, value: string) => {
    headers.value[key] = value;
  };

  return {
    data,
    errorMessage,
    fetchData,
    postData,
    patchData,
    updateHeader,
    deleteData,
    putData,
  };
}
